import React, { useState } from 'react';
import { Box, Typography, Button, CardContent, Card, Divider } from '@mui/material';
import { PaymentElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { CommonContainerBox } from 'gemlux-common-components';
import { ReactComponent as RedWarningShieldSVG } from '../../assets/img/solar-shield-warning-bold.svg';

interface UpdateBillingDetailsProps {
  stripe: Stripe | null;
  elements: StripeElements | null;
  paymentAmount: number | null;
}

const UpdateBillingDetails: React.FC<UpdateBillingDetailsProps> = ({ stripe, elements, paymentAmount }) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (paymentAmount === null || paymentAmount <= 0) {
      setErrorMessage('Invalid payment amount.');
      return;
    }

    const customTitle = 'Account Reactivated!';
    const customDescription =
      'Your account is now reactivated. Use your created login details to access ' +
      'your account and enjoy all the amazing features.';

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.REACT_APP_BASE_URL}/activation-success?title=${encodeURIComponent(
          customTitle,
        )}&description=${encodeURIComponent(customDescription)}`,
      },
    });

    if (result.error) {
      setErrorMessage(result.error.message || 'An unknown error occurred');
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2} marginTop={4}>
      <CommonContainerBox padding="20px" display="flex" flexDirection="column" alignItems="center" gap={2}>
        <RedWarningShieldSVG />
        <Typography variant="title3Bold" textAlign="center" color="text.primary">
          The transaction was unsuccessful
        </Typography>
        <Typography variant="bodyRegular" textAlign="center" color="text.secondary">
          Please update your billing details to proceed.
        </Typography>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Card
            variant="outlined"
            style={{
              width: '100%',
              borderRadius: '4px',
              boxShadow: 'none',
              borderColor: '#EAEAEA',
              marginBottom: '16px',
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginTop: 1 }}>
                <Typography style={{ fontWeight: 'bold' }} variant="button">
                  Total
                </Typography>
                {paymentAmount !== null && (
                  <Typography sx={{ fontWeight: 'bold' }} variant="button">
                    ${paymentAmount.toFixed(2)}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
          <PaymentElement />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!stripe}
            style={{ marginTop: '24px', width: '100%', padding: '10px' }}
          >
            Update Billing Details
          </Button>
          {errorMessage && (
            <Typography style={{ fontWeight: 'bold', color: '#B22A2A', marginTop: '5px' }} variant="button">
              {errorMessage}
            </Typography>
          )}
        </form>
      </CommonContainerBox>
    </Box>
  );
};

export default UpdateBillingDetails;
